import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { X } from "lucide-react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import type { ProntuarioSection } from "@/api/db/medico";
import { MedicoSchemas, type SectionItemInput } from "@/api/router/medico/schemas";
import { UnsavedGuard } from "@/components/shared/form/unsaved-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OverlayShell } from "@/components/ui/overlay";
import { useMedicoEditSection, useMedicoValidateSection } from "@/hooks/use-medico";
import { MedicoLabels } from "@/lib/labels/medico";
import { cn } from "@/lib/utils";

import { EvidenceLink } from "./evidence-link";

type Draft = SectionItemInput;

function toDraft(item: ProntuarioSection["items"][number]): Draft {
	const draft: Draft = {
		key: item.key,
		value: item.value,
		state: item.state === "denied" ? "denied" : "present",
	};

	if (item.evidence !== undefined) {
		draft.evidence = item.evidence;
	}

	if (item.original_term !== undefined) {
		draft.original_term = item.original_term;
	}

	if (item.note !== undefined) {
		draft.note = item.note;
	}

	return draft;
}

export function Section({
	medPatientId,
	section,
	onOpenEvidence,
}: {
	medPatientId: number;
	section: ProntuarioSection;
	onOpenEvidence?: (evidence: string, groupId: string | undefined) => void;
}) {
	const edit = useMedicoEditSection();
	const validate = useMedicoValidateSection();

	const [editing, setEditing] = useState(false);

	const start = () => {
		setEditing(true);
	};

	return (
		<section className="rounded-md border border-border p-3">
			<header className="mb-2 flex items-center justify-between gap-2">
				<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
					<h3 className="text-sm font-semibold text-foreground">
						{MedicoLabels.section[section.section]}
					</h3>

					<span className="text-xs text-muted-foreground">
						{MedicoLabels.itemOrigin[section.lastOrigin]}
					</span>

					{section.validatedBy !== null && (
						<span className="text-xs font-medium text-info">validado</span>
					)}
				</div>

				<div className="flex shrink-0 items-center gap-2">
					<Button variant="ghost" size="sm" onClick={start}>
						Editar
					</Button>

					<Button
						variant="ghost"
						size="sm"
						disabled={validate.isPending}
						onClick={() => validate.mutate({ medPatientId, section: section.section })}
					>
						{validate.isPending && "validando seção..."}
						{!validate.isPending && "Validar seção"}
					</Button>
				</div>
			</header>

			<ul className="flex flex-col gap-1">
				{section.items.map((item) => (
					<li
						key={item.key}
						className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-2"
					>
						<div className="flex min-w-0 flex-col gap-0.5 break-words">
							<span className="text-xs text-muted-foreground">{item.key}</span>
							<span className="text-foreground">{item.value}</span>
						</div>

						<div className="flex shrink-0 items-center gap-2">
							<span className={cn("text-xs", MedicoLabels.itemStateStyle[item.state].text)}>
								{MedicoLabels.itemState[item.state]}
							</span>

							<span className="text-xs text-muted-foreground">
								{MedicoLabels.itemOrigin[item.origin]}
							</span>

							<EvidenceLink
								evidence={item.evidence}
								onOpen={
									onOpenEvidence === undefined
										? undefined
										: (evidence) => onOpenEvidence(evidence, item.extraction_group)
								}
							/>
						</div>
					</li>
				))}
			</ul>

			{editing && (
				<SectionEditModal
					title={MedicoLabels.section[section.section]}
					items={section.items}
					pending={edit.isPending}
					onSave={(items) =>
						edit.mutate(
							{ medPatientId, section: section.section, items },
							{ onSuccess: () => setEditing(false) },
						)
					}
					onClose={() => setEditing(false)}
				/>
			)}
		</section>
	);
}

function SectionEditModal({
	title,
	items,
	pending,
	onSave,
	onClose,
}: {
	title: string;
	items: ProntuarioSection["items"];
	pending: boolean;
	onSave: (items: Draft[]) => void;
	onClose: () => void;
}) {
	const [confirmingClose, setConfirmingClose] = useState(false);
	const {
		control,
		register,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<{ items: Draft[] }>({
		resolver: arktypeResolver(MedicoSchemas.editSection.pick("items")),
		mode: "onChange",
		defaultValues: { items: items.map(toDraft) },
	});
	const { fields, append, remove } = useFieldArray({ control, name: "items" });
	const submit = handleSubmit(({ items }) => onSave(items));

	const handleClose = () => {
		if (isDirty) {
			setConfirmingClose(true);

			return;
		}

		onClose();
	};

	return (
		<OverlayShell
			label={`Editar ${title}`}
			onClose={handleClose}
			className="items-center justify-center p-4"
		>
			<form
				data-component="section-edit-modal"
				onSubmit={submit}
				className="overlay-panel max-w-lg gap-4 p-5"
			>
				<div className="flex items-start justify-between gap-3">
					<div>
						<h2 className="type-section-title text-foreground">Editar seção</h2>
						<p className="mt-0.5 text-xs text-muted-foreground">{title}</p>
					</div>

					<button
						type="button"
						aria-label="Fechar"
						onClick={handleClose}
						className="-mr-1 -mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<X className="size-4" />
					</button>
				</div>

				<div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
					{fields.map((draft, index) => (
						<div key={draft.id} className="flex flex-col gap-2 sm:flex-row sm:items-center">
							<Input
								{...register(`items.${index}.key`)}
								className="w-full sm:w-40"
								placeholder="chave"
							/>

							<Input
								{...register(`items.${index}.value`)}
								className="w-full sm:flex-1"
								placeholder="valor"
							/>

							<div className="flex items-center gap-2">
								<Controller
									control={control}
									name={`items.${index}.state`}
									render={({ field }) => (
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() =>
												field.onChange(field.value === "present" ? "denied" : "present")
											}
										>
											{MedicoLabels.itemState[field.value]}
										</Button>
									)}
								/>

								<Button type="button" variant="ghost" size="sm" onClick={() => remove(index)}>
									Remover
								</Button>
							</div>
						</div>
					))}

					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="w-fit"
						onClick={() => append({ key: "", value: "", state: "present" })}
					>
						Adicionar item
					</Button>
				</div>

				<div className="flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={pending}
						onClick={handleClose}
					>
						Cancelar
					</Button>

					<Button
						type="submit"
						variant="default"
						size="sm"
						className="px-4"
						disabled={!isValid || pending}
					>
						{pending && "salvando seção..."}
						{!pending && "Salvar seção"}
					</Button>
				</div>

				{confirmingClose && (
					<UnsavedGuard onStay={() => setConfirmingClose(false)} onDiscard={onClose} />
				)}
			</form>
		</OverlayShell>
	);
}
